import { resolvePublishedUniform, resolvePublishedRoleUniform, publishedUniformOptions } from './publishedUniformModel.js';
import { publicApiUrl } from './publicApiUrl.js';
import { getEnabledLanguageIds } from '../i18n/languages.js';

const runtimeTrusts = new Map();
const runtimeConfigurations = new Map();
const inFlightConfigurations = new Map();
const CACHE_PREFIX = 'kyc_published_trust:';

function cacheConfiguration(trustId, configuration) {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.setItem(`${CACHE_PREFIX}${trustId}`, JSON.stringify(configuration)); } catch { /* cache is optional */ }
}
function removeCachedConfiguration(trustId) {
  runtimeConfigurations.delete(trustId);
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(`${CACHE_PREFIX}${trustId}`); } catch { /* cache is optional */ }
}

export function getRuntimeTrusts() { return [...runtimeTrusts.values()]; }
export function getRuntimeTrust(trustId) { return runtimeTrusts.get(trustId) || null; }
export function getRuntimeTrustEnabledLanguageIds(trustId) {
  return getEnabledLanguageIds(getRuntimeTrustConfiguration(trustId)?.enabledLanguages);
}

export function isRuntimeTrustLanguageEnabled(trustId, languageId) {
  return getRuntimeTrustEnabledLanguageIds(trustId).includes(String(languageId || '').toLowerCase().split('-')[0]);
}

export function getRuntimeTrustConfiguration(trustId) {
  // Persisted snapshots are not authoritative: access may have been revoked
  // since the last session. A fresh published API response must populate memory.
  return runtimeConfigurations.get(trustId) || null;
}
export function isRuntimeTrustId(trustId) { return runtimeTrusts.has(trustId); }
export function getPublishedUniformById(trustId, uniformId) {
  return resolvePublishedUniform(getRuntimeTrustConfiguration(trustId), uniformId);
}
export function getPublishedUniformOptions(trustId, legacyOptions = []) {
  return publishedUniformOptions(getRuntimeTrustConfiguration(trustId), legacyOptions);
}

export async function loadPublishedTrustDirectory() {
  const response = await fetch(publicApiUrl('/api/published-trusts'), { cache: 'no-store' });
  if (!response.ok) throw new Error(`Published Trust directory returned ${response.status}`);
  const payload = await response.json();
  const trusts = Array.isArray(payload.trusts) ? payload.trusts : [];
  const enabledIds = new Set(trusts.map((trust) => trust.id));
  runtimeTrusts.clear();
  trusts.forEach((trust) => runtimeTrusts.set(trust.id, trust));
  [...runtimeConfigurations.keys()].forEach((trustId) => {
    if (!enabledIds.has(trustId)) removeCachedConfiguration(trustId);
  });
  return getRuntimeTrusts();
}

export async function loadPublishedTrustConfiguration(trustId) {
  if (!trustId) return null;
  // An app start, My Care and a Journey can request the same Trust at once.
  // Share only in-flight requests; retain fresh no-store publication checks
  // on subsequent visits, and preserve immediate revocation behaviour.
  if (inFlightConfigurations.has(trustId)) return inFlightConfigurations.get(trustId);
  const request = (async () => {
    const response = await fetch(publicApiUrl(`/api/published-trust-config/${encodeURIComponent(trustId)}`), { cache: 'no-store' });
    if (!response.ok) {
      removeCachedConfiguration(trustId);
      throw new Error(`Published Trust configuration returned ${response.status}`);
    }
    const payload = await response.json();
    if (payload?.configuration) {
      runtimeConfigurations.set(trustId, payload.configuration);
      cacheConfiguration(trustId, payload.configuration);
      return payload.configuration;
    }
    removeCachedConfiguration(trustId);
    return null;
  })();
  inFlightConfigurations.set(trustId, request);
  try {
    return await request;
  } finally {
    inFlightConfigurations.delete(trustId);
  }
}

export function publishedRoleConfiguration(trustId, role) {
  const configuration = getRuntimeTrustConfiguration(trustId);
  if (!configuration) return null;
  const keys = [role?.role_key, role?.role_id, role?.id].filter(Boolean);
  for (const key of keys) if (configuration.roles?.[key]) return configuration.roles[key];
  return null;
}

export function applyPublishedTrustRole(role, trustId) {
  const local = publishedRoleConfiguration(trustId, role);
  if (!local) return role;
  const configuration = getRuntimeTrustConfiguration(trustId);
  const roleKeys = [role?.role_key, role?.role_id, role?.id].filter(Boolean);
  const fixedParamedic = roleKeys.includes('paramedic');
  const colourId = local?.uniform?.colourId || local?.uniform?.colorId || local?.uniform?.uniformId || null;
  const garmentId = local?.uniform?.garmentId || null;
  const custom = fixedParamedic ? null : resolvePublishedRoleUniform(configuration, local.uniform);
  const legacyId = fixedParamedic ? 'paramedic_green' : (colourId || role.uniform_id || role.uniform_color || 'no_uniform');
  const legacyUniform = colourId ? { ...(role.uniform || {}), id: colourId, token: colourId, garmentId, note: local?.uniform?.note || '' } : role.uniform;
  return {
    ...role,
    trust_id: trustId,
    uniform_id: fixedParamedic ? 'paramedic_green' : (custom?.id || legacyId),
    uniform_color: fixedParamedic ? 'paramedic_green' : (custom?.id || legacyId),
    uniform: fixedParamedic
      ? { id: 'paramedic_green', token: 'paramedic_green', garmentId: 'tunic', trust_id: trustId, note: '' }
      : (custom ? { ...custom, trust_id: trustId, legacyUniformId: legacyId, note: local?.uniform?.note || '' } : legacyUniform),
    care_settings: Array.isArray(local.careSettings) ? local.careSettings : role.care_settings,
  };
}

export function publishedRoleIsIncluded(trustId, role) {
  const configuration = getRuntimeTrustConfiguration(trustId);
  if (!configuration) return true;
  const local = publishedRoleConfiguration(trustId, role);
  return local ? local.included !== false : false;
}
